import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  callCreateInviteKey,
  callListInviteKeys,
  callRestoreInviteKey,
  callRevokeInviteKey,
  type ListedInviteKey,
} from '../functions'

export function AdminKeysScreen() {
  const [keys, setKeys] = useState<ListedInviteKey[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [expiresInDays, setExpiresInDays] = useState<number>(30)
  const [creating, setCreating] = useState(false)
  const [lastCreated, setLastCreated] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await callListInviteKeys({})
      setKeys(res.data.keys)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load keys.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const handleCreate = async () => {
    setCreating(true)
    setError(null)
    try {
      const res = await callCreateInviteKey({
        note: note.trim() || undefined,
        expiresInDays: expiresInDays > 0 ? expiresInDays : undefined,
      })
      setLastCreated(res.data.code)
      setNote('')
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not create key.')
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (code: string) => {
    if (!confirm(`Revoke ${code}? The user will be signed out and lose access immediately.`)) return
    try {
      await callRevokeInviteKey({ code })
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not revoke key.')
    }
  }

  const handleRestore = async (code: string) => {
    if (!confirm(`Restore ${code}? The original account will regain access on next sign-in.`)) return
    try {
      await callRestoreInviteKey({ code })
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not restore key.')
    }
  }

  const copyCode = (code: string) => {
    void navigator.clipboard.writeText(code)
  }

  return (
    <div className="min-h-dvh px-6 py-10 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold">Invite keys</h1>
        <Link to="/" className="text-sm text-text-secondary hover:text-text-primary">
          ← Back to library
        </Link>
      </div>

      <section className="mb-10 p-5 bg-bg-secondary border border-border-primary rounded-lg">
        <h2 className="text-sm uppercase tracking-wide text-text-tertiary mb-4">Create a new key</h2>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Note (who is this for?)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Alice, batch 1"
              className="w-full px-3 py-2 bg-bg-tertiary border border-border-primary rounded-md text-text-primary text-sm focus:outline-none focus:border-accent-primary"
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Expires (days, 0 = never)</label>
            <input
              type="number"
              min={0}
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
              className="w-28 px-3 py-2 bg-bg-tertiary border border-border-primary rounded-md text-text-primary text-sm focus:outline-none focus:border-accent-primary"
            />
          </div>
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className="px-4 py-2 bg-accent-primary hover:bg-accent-hover text-white rounded-md font-medium text-sm disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create key'}
          </button>
        </div>
        {lastCreated && (
          <div className="mt-4 p-3 bg-bg-tertiary border border-accent-primary/40 rounded-md">
            <p className="text-xs text-text-secondary mb-1">New code:</p>
            <div className="flex items-center gap-2">
              <code className="text-base font-mono text-accent-primary">{lastCreated}</code>
              <button
                type="button"
                onClick={() => copyCode(lastCreated)}
                className="text-xs text-text-secondary hover:text-text-primary underline"
              >
                copy
              </button>
            </div>
          </div>
        )}
      </section>

      {error && <p className="mb-4 text-sm text-red-400">{error}</p>}

      {loading ? (
        <p className="text-text-tertiary">Loading…</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-text-tertiary border-b border-border-primary">
            <tr>
              <th className="py-2 pr-3">Code</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Note</th>
              <th className="py-2 pr-3">Created</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.code} className="border-b border-border-primary/50">
                <td className="py-2 pr-3 font-mono">
                  <button
                    type="button"
                    onClick={() => copyCode(k.code)}
                    className="hover:text-accent-primary"
                    title="Click to copy"
                  >
                    {k.code}
                  </button>
                </td>
                <td className="py-2 pr-3">
                  <span
                    className={
                      k.status === 'unused'
                        ? 'text-green-400'
                        : k.status === 'redeemed'
                          ? 'text-text-tertiary'
                          : 'text-red-400'
                    }
                  >
                    {k.status}
                  </span>
                </td>
                <td className="py-2 pr-3 text-text-secondary">{k.note || '—'}</td>
                <td className="py-2 pr-3 text-text-tertiary">
                  {k.createdAt ? new Date(k.createdAt).toLocaleDateString() : '—'}
                </td>
                <td className="py-2 text-right">
                  {(k.status === 'unused' || k.status === 'redeemed') && (
                    <button
                      type="button"
                      onClick={() => handleRevoke(k.code)}
                      className="text-xs text-red-400 hover:underline"
                    >
                      revoke
                    </button>
                  )}
                  {k.status === 'revoked' && (
                    <button
                      type="button"
                      onClick={() => handleRestore(k.code)}
                      className="text-xs text-green-400 hover:underline"
                    >
                      restore
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-text-tertiary">
                  No keys yet. Create one above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}
